import {useContext, useEffect, useState, useCallback} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {
    Alert, Box, Chip, CircularProgress, Snackbar, Tab, Tabs,
} from "@mui/material";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useHasScope} from "../../../../config/globalConfig";
import {
    getTemplate,
    deleteTemplate, duplicateTemplate,
} from "../../api/templateApi";
import {Template, TemplateType, TemplateDetailTabs, extractApiError, getErrorMessage} from "../../types";
import DuplicateTemplateDialog from "../../components/DuplicateTemplateDialog";
import DeleteTemplateDialog from "../../components/DeleteTemplateDialog";
import ResultDialog from "../../components/ResultDialog";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {TabDetails, TabChangelog} from "./tabs";

interface LocationState {
    version?: number | null;
}

const channelColor: Record<TemplateType, "primary" | "secondary" | "success"> = {
    EMAIL: "primary",
    SMS: "secondary",
    PUSH: "success",
};

const TemplateDetail = () => {
    const {name} = useParams<{name: string}>();
    const location = useLocation();
    const navigate = useNavigate();
    const {t} = useTranslation("templates");
    const {globalData} = useContext(GlobalContext);
    const user = globalData?.user;
    const username: string = user?.username || 'unknown';

    // Read version from navigation state
    const initialVersion = (location.state as LocationState)?.version ?? null;

    const canEdit = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_EDIT");
    const canCreate = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_CREATE");
    const canDelete = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_DELETE");
    // Scope "fittizio" presente solo a FE: non esiste sul DB, quindi useHasScope ritorna false
    // e il tab Changelog risulta nascosto. In futuro basterà censire questo scope a backend
    // (assegnandolo ai profili desiderati) per attivarlo senza modifiche al codice FE. 
    const canViewChangelog = useHasScope("SCOPE_NOTIFICATION_HUB_TEMPLATE_VIEW_CHANGELOG");
    // const canViewChangelog = true; // decommentare per forzare la visibilità del tab Changelog

    const [template, setTemplate] = useState<Template | null>(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<TemplateDetailTabs>(TemplateDetailTabs.DETAILS);
    const [duplicateOpen, setDuplicateOpen] = useState(false);
    const [duplicating, setDuplicating] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteResultOpen, setDeleteResultOpen] = useState(false);
    const [duplicateResultOpen, setDuplicateResultOpen] = useState(false);
    const [duplicatedName, setDuplicatedName] = useState<string | null>(null);
    const [snackError, setSnackError] = useState<string | null>(null);
    const [versionToShow, setVersionToShow] = useState<number | null>(initialVersion);

    const loadTemplate = useCallback((templateName: string) => {
        setLoading(true);
        getTemplate(templateName)
            .then((data) => {
                // Sort versions in descending order (newest first)
                if (data.versions) {
                    data.versions.sort((a, b) => b.versionNumber - a.versionNumber);
                }
                setTemplate(data);
            })
            .catch(() => {
                navigate("/notification-hub/list", { state: { snackError: t('detailErrors.notFound') } });
            })
            .finally(() => setLoading(false));
    }, [t, navigate]);

    useEffect(() => {
        if (!name) return;
        loadTemplate(name);
    }, [name, loadTemplate]);

    const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: number) => {
        setTab(newValue);
    }, []);

    const handleDelete = async () => {
        if (!name) return;
        setDeleting(true);
        try {
            await deleteTemplate(name, username);
            setDeleteOpen(false);
            setDeleteResultOpen(true);
        } catch {
            setSnackError(t('detailErrors.delete'));
        } finally {
            setDeleting(false);
        }
    };

    const handleDuplicate = async (newName: string) => {
        if (!name) return;
        setDuplicating(true);
        try {
            await duplicateTemplate(name, newName, username);
            setDuplicateOpen(false);
            setDuplicatedName(newName);
            setDuplicateResultOpen(true);
        } catch (e: any) {
            const apiError = extractApiError(e);
            if (apiError) {
                const errorMsg = getErrorMessage(apiError.code, t);
                setSnackError(errorMsg);
            } else {
                setSnackError(t('detailErrors.duplicate'));
            }
        } finally {
            setDuplicating(false);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
                <CircularProgress/>
            </Box>
        );
    }

    if (!template) {
        return null;
    }

    return (
        <>
            <SectionHeader title={t('detailHeader.label')} subtitle={
                <Box component="span" sx={{display: 'inline-flex', alignItems: 'center', gap: 1, flexWrap: 'wrap'}}>
                    {`${t('editHeader.templateName')}: ${template.name}`}
                    {template.type && <Chip label={template.type} color={channelColor[template.type] || "default"} size="small"/>}
                </Box>
            }/>

            {/* Tabs */}
            <Box px={2} pt={2}>
                <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                    <Tabs value={tab} onChange={handleChangeTab}>
                        <Tab label={t('tabs.details', 'Dettaglio Template')} value={TemplateDetailTabs.DETAILS} />
                        {canViewChangelog && (
                            <Tab label={t('tabs.changelog', 'Changelog')} value={TemplateDetailTabs.CHANGELOG} />
                        )}
                    </Tabs>
                </Box>

                {/* Tab Content */}
                {tab === TemplateDetailTabs.DETAILS && (
                    <TabDetails 
                        key={`${template.name}-${versionToShow ?? 'active'}`}
                        template={template} 
                        username={username}
                        initialVersion={versionToShow}
                        onReload={(showVersion) => {
                            setVersionToShow(showVersion ?? null);
                            loadTemplate(name!);
                        }}
                        onError={setSnackError}
                        canEdit={canEdit}
                        canCreate={canCreate}
                        canDelete={canDelete}
                        onEdit={(versionNumber) => {
                            navigate(`/notification-hub/edit/${template.name}`, {
                                state: { version: versionNumber }
                            });
                        }}
                        onDuplicate={() => setDuplicateOpen(true)}
                        onDelete={() => setDeleteOpen(true)}
                    />
                )}
                {tab === TemplateDetailTabs.CHANGELOG && canViewChangelog && (
                    <TabChangelog templateName={template.name} />
                )}
            </Box>

            <DuplicateTemplateDialog
                open={duplicateOpen}
                originalName={name || ""}
                templateType={template.type}
                onClose={() => setDuplicateOpen(false)}
                onConfirm={handleDuplicate}
                loading={duplicating}
            />

            <DeleteTemplateDialog
                open={deleteOpen}
                templateNames={name ? [name] : []}
                onClose={() => setDeleteOpen(false)}
                onConfirm={handleDelete}
                loading={deleting}
            />

            <ResultDialog
                open={deleteResultOpen}
                success
                title={t('deleteResultDialog.successTitle')}
                message={t('deleteResultDialog.successMessageSingle')}
                onClose={() => {
                    setDeleteResultOpen(false);
                    navigate("/notification-hub/list");
                }}
            />

            <ResultDialog
                open={duplicateResultOpen}
                success
                title={t('duplicateResultDialog.successTitle')}
                message={t('duplicateResultDialog.successMessage')}
                onClose={() => {
                    setDuplicateResultOpen(false);
                    if (duplicatedName) {
                        window.open(`/notification-hub/view/${duplicatedName}`, '_blank');
                        setDuplicatedName(null);
                    }
                }}
            />

            <Snackbar open={!!snackError} autoHideDuration={5000} onClose={() => setSnackError(null)} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Alert severity="error" onClose={() => setSnackError(null)} variant="filled">{snackError}</Alert>
            </Snackbar>
        </>
    );
};

export default TemplateDetail;
